import { Controller } from "stimulus"
import { celebratePoints } from "../services/points_celebration_service"

// This controller intentionally spans several concerns (modal/form, type
// dropdown, debt CRUD, card rendering, expand/collapse, snapshot) for a single
// page. Split it into debt-tracker-form / debt-tracker-list /
// debt-tracker-snapshot controllers (coordinated via Stimulus outlets) and
// extract the modal/card/snapshot into ViewComponents once a second flow reuses
// this pattern (planned for M3+). Splitting now would be premature abstraction.
export default class extends Controller {
  static get targets() {
    return [
      "typeDropdownButton",
      "typeDropdownList",
      "typeDropdownSelectedIcon",
      "typeDropdownSelectedName",
      "typeDropdownChevron",
      "lenderName",
      "balanceOwed",
      "interestRate",
      "minPayment",
      "submitBtn",
      "debtTypeSelect",
      "modal",
      "actionButtons"
    ]
  }

  static get values() {
    return {
      selectedType: String,
      selectedTypeName: String,
      celebrate: Boolean,
    }
  }

  connect() {
    this._ready = true
    this._boundOnDocumentClick = this._onDocumentClick.bind(this)
    document.addEventListener("click", this._boundOnDocumentClick)

    const modal = this._addDebtModal()
    if (modal) {
      this._boundOnModalShow = this._onModalShow.bind(this)
      this._boundOnModalHide = this._onModalHide.bind(this)
      $(modal).on("show.bs.modal", this._boundOnModalShow)
      $(modal).on("hide.bs.modal", this._boundOnModalHide)
    }

    // Fires once, only when arriving from a fresh completion (the controller
    // sets the flag via flash on the complete redirect); a returning completed
    // user loads the snapshot without it.
    if (this.celebrateValue) celebratePoints(this.element)
  }

  disconnect() {
    document.removeEventListener("click", this._boundOnDocumentClick)

    const modal = this._addDebtModal()
    if (modal && this._boundOnModalShow) {
      $(modal).off("show.bs.modal", this._boundOnModalShow)
      $(modal).off("hide.bs.modal", this._boundOnModalHide)
      $(modal).modal("hide")
    }
  }

  // ─── Type dropdown ────────────────────────────────────────────────────

  toggleTypeDropdown(event) {
    event.stopPropagation()
    const list = this._modalTarget("typeDropdownList")
    if (!list) return
    if (!list.hidden) {
      this._closeTypeDropdown()
    } else {
      this._openTypeDropdown()
    }
  }

  _openTypeDropdown() {
    const list = this._modalTarget("typeDropdownList")
    const button = this._modalTarget("typeDropdownButton")
    const chevron = this._modalTarget("typeDropdownChevron")
    if (!list || !button || !chevron) return
    list.hidden = false
    button.setAttribute("aria-expanded", "true")
    chevron.style.transform = "rotate(180deg)"
  }

  _closeTypeDropdown() {
    const list = this._modalTarget("typeDropdownList")
    const button = this._modalTarget("typeDropdownButton")
    const chevron = this._modalTarget("typeDropdownChevron")
    if (!list || !button || !chevron) return
    list.hidden = true
    button.setAttribute("aria-expanded", "false")
    chevron.style.transform = "rotate(0deg)"
  }

  selectDropdownType(event) {
    const button = event.currentTarget
    const type = button.dataset.debtType
    const label = button.dataset.debtLabel

    this._setDropdownSelection(type, label)
    this._closeTypeDropdown()
  }

  _setDropdownSelection(type, label) {
    this.selectedTypeValue = type || ""
    this.selectedTypeNameValue = label || ""
    const selectedName = this._modalTarget("typeDropdownSelectedName")
    if (selectedName) selectedName.textContent = label || ""
    this._renderSelectedTypeIcon(type)
    const debtTypeSelect = this._modalTarget("debtTypeSelect")
    if (debtTypeSelect) debtTypeSelect.value = type || ""
    this._syncFormSubmitButton(this._addForm())
  }

  _renderSelectedTypeIcon(type) {
    const iconTarget = this._modalTarget("typeDropdownSelectedIcon")
    const list = this._modalTarget("typeDropdownList")
    if (!iconTarget) return
    iconTarget.replaceChildren()
    if (!type || !list) return
    const sourceItem = list.querySelector(`[data-debt-type="${CSS.escape(type)}"]`)
    if (!sourceItem) return
    const sourceSvg = sourceItem.querySelector("svg")
    if (sourceSvg) iconTarget.appendChild(sourceSvg.cloneNode(true))
  }

  // ─── Open ────────────────────────────────────────────────────────────

  openAddDebt(event) {
    this._showModal(this._addDebtModal(), event.currentTarget)
  }

  // Per-card edit modal; the pencil button carries its modal id.
  openEditDebt(event) {
    const id = event.currentTarget.dataset.editModalId
    const modal = id ? document.getElementById(id) : null
    this._showModal(modal, event.currentTarget)
    const form = modal?.querySelector("form[id^='edit-form-']")
    if (form) this._syncFormSubmitButton(form)
  }

  _showModal(modalEl, relatedTarget) {
    if (!modalEl) return
    if (!modalEl.classList.contains("show")) {
      const instance = $(modalEl).data("bs.modal")
      if (instance) {
        instance._isShown = false
        instance._isTransitioning = false
      }
      this._cleanupBackdrop()
    }
    $(modalEl).modal("show", relatedTarget)
  }

  // ─── Turbo submit-end handlers ───────────────────────────────────────

  onAddDebtSuccess(event) {
    if (!event.detail.success) return
    this._closeBootstrapModal(this._addDebtModal())
  }

  onDestroyDebtSuccess(event) {
    if (!event.detail.success) return
    this._cleanupBackdrop()
  }

  onDelinkLiabilitySuccess(event) {
    if (!event.detail.success) return
    const modalId = event.target.dataset.delinkModalId
    this._closeBootstrapModal(modalId ? document.getElementById(modalId) : null)
  }

  onEditDebtSuccess(event) {
    if (!event.detail.success) return
    const modal = event.target.closest(".modal")
    if (modal) {
      const errorEl = document.getElementById(modal.dataset.errorTarget)
      if (errorEl) errorEl.innerHTML = ""
    }
    this._closeBootstrapModal(modal)
  }

  _closeBootstrapModal(modalEl) {
    if (!modalEl) { this._cleanupBackdrop(); return; }
    // Remove fade so hidden.bs.modal fires synchronously — if a Turbo stream
    // removes the element from the DOM before the CSS transition ends, the
    // transitionend event never fires and the backdrop gets stuck.
    // Restore fade after hiding so future opens still animate.
    modalEl.classList.remove('fade')
    $(modalEl).one('hidden.bs.modal', () => {
      modalEl.classList.add('fade')
      this._cleanupBackdrop()
    })
    $(modalEl).modal('hide')
  }

  _cleanupBackdrop() {
    $(".modal-backdrop").remove()
    $(document.body).removeClass("modal-open").css("padding-right", "")
  }

  // Called by Bootstrap's show.bs.modal before the modal fades in.
  // event.relatedTarget is the button that triggered the modal (type grid
  // button or "Add another"), which carries data-debt-type / data-debt-label.
  _onModalShow(event) {
    const btn = event.relatedTarget
    const type = btn?.dataset?.debtType || ""
    const label = btn?.dataset?.debtLabel || ""
    this._clearFormFields()
    this._setDropdownSelection(type, label)
  }

  _onModalHide() {
    this._closeTypeDropdown()
  }

  // ─── Form helpers ────────────────────────────────────────────────────

  // Cap for minPayment submit gating. Input itself is hard-limited to 7
  // integer digits (max 9,999,999.xx) via formatted-number; this stays just
  // above that so decimals like 9,999,999.99 still pass.
  static get MAX_AMOUNT() {
    return 10_000_000
  }

  // data-action="input->debt-tracker#updateSubmitState" — scopes to the form
  // that fired the event so edit modals don't read the add-modal fields.
  updateSubmitState(event) {
    this._syncFormSubmitButton(this._formFromEvent(event))
  }

  validateBeforeSubmit(event) {
    const form = event.target
    if (this._formIsComplete(form)) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this._syncFormSubmitButton(form)
  }

  checkBeforeInsight() {
    const container = document.getElementById("debt-action-buttons")
    if (container?.dataset.hasInvalidDebts !== "true") {
      this._submitInsightForm()
      return
    }
    this._showModal(document.getElementById("negative-amortization-warning-modal"))
  }

  ignoreInvalidDebts() {
    const container = this._actionButtonsContainer()
    if (!container) return
    const form = container.querySelector("form")
    form.querySelectorAll('[name="acknowledge_invalid"]').forEach(el => el.remove())
    const flag  = document.createElement("input")
    flag.type   = "hidden"
    flag.name   = "acknowledge_invalid"
    flag.value  = "true"
    form.appendChild(flag)
    this._cleanupBackdrop()
    this._submitInsightForm()
  }

  fixInvalidDebts() {
    const container = this._actionButtonsContainer()
    if (!container) return
    const form = container.querySelector("form")
    form.querySelectorAll('[name="fix_adjustments"]').forEach(el => el.remove())
    const flag  = document.createElement("input")
    flag.type   = "hidden"
    flag.name   = "fix_adjustments"
    flag.value  = "true"
    form.appendChild(flag)
    this._cleanupBackdrop()
    this._submitInsightForm()
  }

  _submitInsightForm() {
    const container = this._actionButtonsContainer()
    if (!container) return
    container.querySelector("form")?.requestSubmit()
  }

  _clearFormFields() {
    const lenderName = this._modalTarget("lenderName")
    const balanceOwed = this._modalTarget("balanceOwed")
    const interestRate = this._modalTarget("interestRate")
    const minPayment = this._modalTarget("minPayment")
    if (lenderName) lenderName.value = ""
    if (balanceOwed) balanceOwed.value = ""
    if (interestRate) interestRate.value = ""
    if (minPayment) minPayment.value = ""
    const errorEl = document.getElementById("add-debt-modal-error")
    if (errorEl) errorEl.innerHTML = ""
  }

  _formFromEvent(event) {
    const el = event?.target
    if (!el) return this._addForm()

    return el.closest("form") ||
      el.closest(".modal")?.querySelector("form[id^='edit-form-'], #add-debt-form") ||
      this._addForm()
  }

  _addForm() {
    return this._addDebtModal()?.querySelector("#add-debt-form") ?? null
  }

  _syncFormSubmitButton(form) {
    if (!form) return
    const submitBtn = this._submitButtonFor(form)
    if (!submitBtn) return

    submitBtn.disabled = !this._formIsComplete(form)
  }

  _submitButtonFor(form) {
    if (!form) return null
    if (form.id === "add-debt-form") return this._modalTarget("submitBtn")
    if (!form.id) return null
    return document.querySelector(`button[type="submit"][form="${CSS.escape(form.id)}"]`)
  }

  _formIsComplete(form) {
    const fields = this._fieldsFromForm(form)
    if (!fields.debtType?.toString().trim()) return false
    if (!fields.lenderName?.toString().trim()) return false
    if (!this._hasNumericInput(fields.balanceOwed) || this._parseAmount(fields.balanceOwed) <= 0) return false
    // Interest may be 0; the field itself must still be filled.
    if (!this._hasNumericInput(fields.interestRate)) return false
    if (!this._hasNumericInput(fields.minPayment) || this._parseAmount(fields.minPayment) <= 0) return false
    if (this._parseAmount(fields.minPayment) > this.constructor.MAX_AMOUNT) return false
    return true
  }

  _fieldsFromForm(form) {
    if (form.id === "add-debt-form") {
      return {
        debtType: this.selectedTypeValue,
        lenderName: this._modalTarget("lenderName")?.value,
        balanceOwed: this._modalTarget("balanceOwed")?.value,
        interestRate: this._modalTarget("interestRate")?.value,
        minPayment: this._modalTarget("minPayment")?.value,
      }
    }

    return {
      debtType: this._formFieldValue(form, "debt[debt_type]"),
      lenderName: this._formFieldValue(form, "debt[lenderName]"),
      balanceOwed: this._formFieldValue(form, "debt[balanceOwed]"),
      interestRate: this._formFieldValue(form, "debt[interestRate]"),
      minPayment: this._formFieldValue(form, "debt[minPayment]"),
    }
  }

  _formFieldValue(form, name) {
    const field = form.elements.namedItem(name)
    if (!field) return ""
    // RadioNodeList (duplicate names) — take the first element's value.
    return field.value ?? field[0]?.value ?? ""
  }

  _hasNumericInput(val) {
    const cleaned = (val || "").toString().replace(/[^0-9.]/g, "")
    if (!cleaned || cleaned === ".") return false
    return Number.isFinite(Number(cleaned))
  }

  _parseAmount(val) {
    return Number((val || "").toString().replace(/[^0-9.]/g, "")) || 0
  }

  // ─── Global handlers ─────────────────────────────────────────────────

  // Bootstrap reparents the modal to <body>, so Stimulus targets inside it are
  // no longer descendants of this.element. Resolve by stable id instead.
  _addDebtModal() {
    return document.getElementById("add-debt-modal")
  }

  _modalTarget(name) {
    return this._addDebtModal()?.querySelector(`[data-debt-tracker-target="${name}"]`) ?? null
  }

  _actionButtonsContainer() {
    if (this.hasActionButtonsTarget && this.actionButtonsTarget.isConnected) {
      return this.actionButtonsTarget
    }
    return document.getElementById("debt-action-buttons")
  }

  _onDocumentClick(event) {
    const list = this._modalTarget("typeDropdownList")
    if (!list || list.hidden) return
    const inDropdown = event.target.closest("[data-debt-tracker-target='typeDropdownButton']") ||
      event.target.closest("[data-debt-tracker-target='typeDropdownList']")
    if (!inDropdown) this._closeTypeDropdown()
  }
}
